import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ConsentForm } from "../components/ConsentForm";

// Keep provider numbers as strings to preserve leading zeroes and letters.
const normalizeProviderNumber = (value) => String(value ?? "").trim().toUpperCase();
const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");
const pickFirst = (...values) =>
  values.map((value) => String(value ?? "").trim()).find(Boolean) || "";
const safeFileName = (value) =>
  String(value || "consent").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 120) || "consent";
const getTodayLocal = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const getError = async (response, fallback) => {
  const text = await response.text();
  try { return JSON.parse(text)?.error || fallback; } catch { return text || fallback; }
};
const addressOf = (row) =>
  [row?.address1, row?.city, row?.state, row?.zip_code].filter(Boolean).join(", ");
const adaptFacility = (row) => ({
  ...row,
  name: pickFirst(row.facility_name, row.name),
  providerNumber: normalizeProviderNumber(row.provider_number),
  address: addressOf(row),
  facilityUniqueId: pickFirst(row.facility_unique_id),
});

export default function NewConsentFormPage() {
  const { facilityCode, facilityUniqueId: facilityUniqueIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const facilityNameFromUrl = searchParams.get("facility_name") || searchParams.get("facilityName") || "";
  const providerFromPath = facilityUniqueIdParam ? "" : normalizeProviderNumber(facilityCode);
  const fuiFromPath = facilityUniqueIdParam ? String(facilityUniqueIdParam).trim() : "";
  const hasFixedFacility = Boolean(providerFromPath || fuiFromPath);
  const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const [consentSubmitted, setConsentSubmitted] = useState(false);

  const [facility, setFacility] = useState(null);
  const [facilityResults, setFacilityResults] = useState([]);
  const [facilityQuery, setFacilityQuery] = useState(facilityNameFromUrl);
  const [loading, setLoading] = useState(hasFixedFacility);
  const [searching, setSearching] = useState(false);
  const [facilityError, setFacilityError] = useState("");
  const controllerRef = useRef(null);

  const lookupFacility = useCallback(async ({ provider = "", fui = "", name = "", autoSelect = false }) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setFacilityError("");
    setSearching(true);
    try {
      let url;
      if (fui) {
        url = `${apiBase}/external_form_facility_info_lookup/fui/${encodeURIComponent(fui)}`;
      } else {
        const query = new URLSearchParams({ provider_number: normalizeProviderNumber(provider), facility_name: name });
        url = `${apiBase}/external_form_facility_info_lookup?${query.toString()}`;
      }
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(await getError(response, "Unable to look up facility."));
      const data = await response.json();
      const rows = Array.isArray(data?.rows) ? data.rows.slice(0, 100) : [];
      setFacilityResults(rows);
      if (autoSelect) {
        if (!rows.length) throw new Error("Facility not found. Check the facility URL or provider number.");
        setFacility(adaptFacility(rows[0]));
      }
    } catch (error) {
      if (error?.name === "AbortError") return;
      setFacilityError(error.message || "Facility lookup failed.");
      if (autoSelect) setFacility(null);
      setFacilityResults([]);
    } finally {
      if (!controller.signal.aborted) {
        setSearching(false);
        setLoading(false);
      }
    }
  }, [apiBase]);

  useEffect(() => {
    setFacility(null);
    if (hasFixedFacility) {
      setLoading(true);
      lookupFacility({ provider: providerFromPath, fui: fuiFromPath, autoSelect: true });
    } else {
      setLoading(false);
    }
    return () => controllerRef.current?.abort();
  }, [providerFromPath, fuiFromPath, hasFixedFacility, lookupFacility]);

  useEffect(() => {
    if (hasFixedFacility || facilityQuery.trim().length < 3) {
      if (!hasFixedFacility) {
        controllerRef.current?.abort();
        setSearching(false);
        setFacilityResults([]);
      }
      return;
    }
    const timeout = setTimeout(() => {
      lookupFacility({ name: facilityQuery.trim() });
    }, 400);
    return () => clearTimeout(timeout);
  }, [facilityQuery, hasFixedFacility, lookupFacility]);

  const checkProxyPhone = useCallback(async (tenDigits, signal) => {
    if (digitsOnly(tenDigits).length !== 10) throw new Error("Enter a 10-digit proxy phone number.");
    const response = await fetch(`${apiBase}/consent/line-type`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone: `+1${tenDigits}` }),
      signal,
    });
    if (!response.ok) throw new Error(await getError(response, "Phone line verification failed."));
    return response.json();
  }, [apiBase]);

  const handleSubmit = useCallback(async (submission) => {
    if (!facility?.name || !(facility?.providerNumber || facility?.facilityUniqueId)) {
      throw new Error("A valid facility must be selected before submitting.");
    }
    const patientOpt = submission.consentChoice === "opt_in" ? "optin" :
      submission.consentChoice === "opt_out" ? "optout" : null;
    if (!patientOpt) throw new Error("Select Opt In or Opt Out.");
    if (!submission.signatureDataUrl) throw new Error("A signature is required.");

    const patient = {
      firstName: submission.patient.firstName,
      lastName: submission.patient.lastName,
      dob: submission.patient.dateOfBirth,
      email: "",
      phoneNumber: "",
      provider_number: facility.providerNumber,
      facility_unique_id: facility.facilityUniqueId,
      facility_name: facility.name,
      corp_name: facility.corp_name || "",
      address1: facility.address1 || "",
      city: facility.city || "",
      state: facility.state || "",
      zip_code: facility.zip_code || "",
    };
    const isProxy = submission.completedBy === "proxy";
    const proxy = isProxy ? submission.proxy : null;
    const phoneDigits = digitsOnly(proxy?.phone);
    const phoneVerification = submission.proxyLineCheck;
    if (isProxy && (phoneDigits.length !== 10 || phoneVerification?.valid !== true ||
      phoneVerification?.number !== phoneDigits)) {
      throw new Error("Verify a valid proxy phone number before submitting.");
    }
    const signedOn = getTodayLocal();
    // Match the existing /consent/pdf/informed-consent request exactly.
    const pdfResponse = await fetch(`${apiBase}/consent/pdf/informed-consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        patientOpt,
        signedOn,
        patient: {
          name: `${patient.firstName} ${patient.lastName}`.trim(),
          dob: patient.dob,
          phone: patient.phoneNumber,
          email: patient.email,
          facility_name: patient.facility_name,
          provider_number: patient.provider_number,
          facility_unique_id: patient.facility_unique_id,
        },
        proxy: isProxy ? {
          name: `${proxy.firstName} ${proxy.lastName}`.trim(),
          phone: proxy.phone,
          email: proxy.email,
          relationship: proxy.relationship,
        } : null,
        signatureDataUrl: submission.signatureDataUrl,
        consentMethod: "Electronic",
      }),
    });
    if (!pdfResponse.ok) throw new Error(await getError(pdfResponse, "Failed to generate consent PDF."));
    const pdfBlob = await pdfResponse.blob();
    if (!pdfBlob.size) throw new Error("PDF generation returned an empty file.");
    const pdfFile = new File(
      [pdfBlob],
      `Informed-Consent-${safeFileName(`${patient.firstName} ${patient.lastName}`)}-${signedOn}.pdf`,
      { type: "application/pdf" },
    );

    const line = phoneVerification?.response || null;
    const payload = {
      patient,
      facility: { ...facility, provider_number: facility.providerNumber, facility_unique_id: facility.facilityUniqueId },
      isProxy,
      proxy,
      patientOpt,
      patientContact: { phone_e164: "", lineTypeResponse: null, sms_capable: false, line_type: "", valid: null },
      proxyContact: isProxy ? {
        phone_e164: `+1${phoneDigits}`,
        lineTypeResponse: line,
        sms_capable: phoneVerification?.valid === true,
        line_type: String(line?.lineType || ""),
        valid: line?.valid ?? null,
      } : null,
      providerNumberFromPath: providerFromPath || null,
      facilityUniqueIdFromPath: fuiFromPath || null,
      lookupTypeFromPath: fuiFromPath ? "fui" : "provider_number",
      informedConsentPdf: { name: pdfFile.name, type: pdfFile.type, size: pdfFile.size },
    };
    const formData = new FormData();
    formData.append("file", pdfFile);
    formData.append("payload", JSON.stringify(payload));
    const submitResponse = await fetch(`${apiBase}/consent/insert-external-form`, {
      method: "POST",
      body: formData,
    });
    if (!submitResponse.ok) throw new Error(await getError(submitResponse, "Consent submission failed."));
    const saved = await submitResponse.json().catch(() => ({}));
    return { ...saved, pdfFile };
  }, [apiBase, facility, providerFromPath, fuiFromPath]);

  const facilityOptions = useMemo(() => facilityResults.map((row, i) => ({
    id: i,
    row,
    label: `${row.facility_name || "Unknown facility"} (${row.provider_number || "N/A"})${row.city ? ` · ${row.city}` : ""}`,
  })), [facilityResults]);

  return (
    <main className="tcf-page">
      <div className="tcf-page-container">
        {!hasFixedFacility && (
          <div className="tcf" style={{ marginBottom: 24 }}>
            <label className="tcf-label" htmlFor="tcf-facility-search">Search for a facility</label>
            <input id="tcf-facility-search" className="tcf-input" value={facilityQuery}
              placeholder="Enter at least 3 characters" onChange={(e) => {
                setFacilityQuery(e.target.value);
                setFacility(null);
              }} />
            {searching && <p role="status">Searching facilities…</p>}
            {facilityOptions.length > 0 && (
              <select className="tcf-input" aria-label="Select a facility" value={facilityOptions.find((option) => option.row.facility_unique_id === facility?.facilityUniqueId && option.row.provider_number === facility?.providerNumber)?.id ?? ""}
                style={{ marginTop: 12 }} onChange={(e) => {
                  const found = facilityOptions.find((option) => String(option.id) === e.target.value);
                  setFacility(found ? adaptFacility(found.row) : null);
                }}>
                <option value="">Choose a matching facility</option>
                {facilityOptions.map((option) => (
                  <option key={`${option.id}-${option.row.facility_unique_id}`} value={String(option.id)}>{option.label}</option>
                ))}
              </select>
            )}
          </div>
        )}
        {loading ? <p role="status">Loading facility information…</p> : null}
        {facilityError ? <p className="tcf-error" role="alert">{facilityError}</p> : null}
        {!loading && !facility && !facilityError ? <p>Select a facility to continue.</p> : null}
        {!loading && facility && (
          <>
            <h1 className="tcf-page-title">
              {consentSubmitted
                  ? facility.name
                  : `Agree to enroll in advanced monitoring services for your loved one at ${facility.name}`
              }
            </h1>
            <ConsentForm key={facility.facilityUniqueId || facility.providerNumber}
              facility={facility}
              checkProxyPhone={checkProxyPhone}
              onSubmit={handleSubmit}
                         onSuccess={() => setConsentSubmitted(true)}
              // Existing backend contact-request and signed-copy email endpoints were not supplied.
              // Avoid exposing actions that would imply these messages were sent.
              links={{}}
            />
          </>
        )}
      </div>
    </main>
  );
}
